import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FetcherWithComponents } from 'react-router';
import { useFetcher } from 'react-router';
import type { PostAuthOptions } from 'types';

import ModalNav from '~/components/Modals/ModalNav';
import type {
  NotificationData,
  NotificationModalStep
} from '~/components/ProductNotificationButton';
import { recordPendingRegistration } from '~/hooks/useIsPendingConfirmation';
import { RegistrationEntryProvider } from '~/hooks/useRegistrationInteraction';
import gtm from '~/services/gtm.client';

import AuthDialog from '../AuthDialog';
import InitialScreen from '../InitialScreen';
import EmailLogin from '../LoginFlow';
import CancelNotifications from './CancelNotifications';
import ConfirmEmailReminder from './ConfirmEmailReminder';
import EmailEntry from './EmailEntry';
import EmailSubmitted from './EmailSubmitted';
import EmailSubmitting from './EmailSubmitting';

enum Stage {
  Error = -1,
  Initial = 0,
  EmailInput = 1,
  EmailSubmitting = 2,
  EmailRegistered = 3,
  Login = 4,
  Cancel = 5,
  ExitReminder = 6
}

const INTERCEPTED_STAGES = [Stage.Initial, Stage.Login, Stage.EmailInput];

export type FetcherData = {
  email?: string;
  errors?: {
    conflict?: boolean;
  };
};

function getStage(fetcher: FetcherWithComponents<FetcherData>) {
  const fetcherData = fetcher.data;

  function getIdleStage() {
    if (!fetcher.data) {
      return Stage.EmailInput;
    }
    if (!fetcherData?.errors) {
      return Stage.EmailRegistered;
    }

    return Stage.EmailInput;
  }

  switch (fetcher.state) {
    case 'idle':
      return getIdleStage();
    case 'submitting':
    case 'loading':
      return Stage.EmailSubmitting;
  }
}

type Props = {
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  onModalStep: (modalStep: NotificationModalStep) => void;
  postAuthOptions: PostAuthOptions;
  product: NotificationData;
};

export default function EmailSignUpForNotification(props: Props) {
  return (
    <RegistrationEntryProvider
      entry={{ placement: 'notify_me', productId: String(props.product.id) }}
    >
      <NotificationSignUp {...props} />
    </RegistrationEntryProvider>
  );
}

function NotificationSignUp({
  onDismiss,
  onModalStep,
  postAuthOptions,
  product
}: Props) {
  const [stage, setStage] = useState(Stage.Initial);
  const [previousStage, setPreviousStage] = useState<Stage>(Stage.Initial);

  const fetcher = useFetcher<FetcherData>();

  useEffect(() => {
    if (stage === Stage.Initial || stage >= Stage.Login) return;

    const newStage = getStage(fetcher) ?? Stage.Initial;
    setStage(newStage);

    if (
      newStage === Stage.EmailRegistered &&
      postAuthOptions.action === 'restock'
    ) {
      recordPendingRegistration(fetcher.data?.email);
    }
  }, [fetcher, stage, postAuthOptions]);

  function handleDismiss(options?: { loginSuccessful?: boolean }) {
    if (options?.loginSuccessful) {
      onDismiss(options);

      return;
    }

    if (stage === Stage.EmailRegistered) {
      setStage(Stage.ExitReminder);

      return;
    }

    if (INTERCEPTED_STAGES.includes(stage)) {
      gtm.trackedButtonClick({
        buttonName: 'X',
        pagePosition: 'Sign in modal'
      });
      onModalStep({ element: 'close', step: 'sign_in' });
      setPreviousStage(stage);
      setStage(Stage.Cancel);

      return;
    }

    onDismiss();
  }

  function onBackNavigation() {
    setStage(stage - 1);
  }

  if (stage === Stage.Login) {
    return (
      <EmailLogin
        onBackNavigation={() => setStage(Stage.Initial)}
        modalType="notifications"
        onDismiss={handleDismiss}
        postAuthOptions={postAuthOptions}
      />
    );
  }

  if (stage === Stage.Cancel) {
    return (
      <CancelNotifications
        image={product.image}
        onGoBack={() => {
          onModalStep({ element: 'cancel_declined', step: 'sign_in' });
          setStage(previousStage);
        }}
        onCancel={() => {
          onModalStep({ element: 'cancel_confirmed', step: 'sign_in' });
          onDismiss();
        }}
      />
    );
  }

  if (stage === Stage.ExitReminder) {
    return (
      <ConfirmEmailReminder
        email={fetcher.data?.email ?? ''}
        image={product.image}
        onDismiss={onDismiss}
      />
    );
  }

  return (
    <AuthDialog onDismiss={() => handleDismiss()}>
      {stage === Stage.Initial ? (
        <InitialScreen
          onDismiss={handleDismiss}
          onSignUpClick={() => setStage(Stage.EmailInput)}
          postAuthOptions={postAuthOptions}
          onLogInClick={() => {
            setStage(Stage.Login);
          }}
          image={product.image}
          modalType="notifications"
          tempContext="Notification Flow"
        />
      ) : (
        <RegisterScreen
          onBackNavigation={
            stage < Stage.EmailSubmitting ? onBackNavigation : undefined
          }
          onDismiss={handleDismiss}
          fetcher={fetcher}
          product={product}
          sizes={postAuthOptions.sizes ?? []}
          stage={stage}
        />
      )}
    </AuthDialog>
  );
}

type RegisterScreenProps = {
  fetcher: FetcherWithComponents<FetcherData>;
  onBackNavigation?: () => void;
  onDismiss: Props['onDismiss'];
  product: Props['product'];
  sizes: string[];
  stage: Stage;
};

function RegisterScreen({
  onBackNavigation,
  onDismiss,
  stage,
  fetcher,
  product,
  sizes
}: RegisterScreenProps) {
  const { t } = useTranslation();

  return (
    <>
      <ModalNav
        heading={
          stage === Stage.EmailRegistered
            ? t('login.stageComplete.title')
            : t('login.stageSignUp.title')
        }
        onBack={onBackNavigation}
      />
      <MainBody
        stage={stage}
        fetcher={fetcher}
        product={product}
        sizes={sizes}
        onDismiss={onDismiss}
      />
    </>
  );
}

type MainBodyProps = {
  fetcher: FetcherWithComponents<FetcherData>;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  product: NotificationData;
  sizes: string[];
  stage: Stage;
};

function MainBody({
  fetcher,
  product,
  sizes,
  onDismiss,
  stage
}: MainBodyProps) {
  const { t } = useTranslation();
  const fetcherData = fetcher.data as {
    email?: string;
    errors?: { conflict: boolean };
  };

  switch (stage) {
    case Stage.EmailInput:
      return <EmailEntry product={product} fetcher={fetcher} sizes={sizes} />;
    case Stage.EmailSubmitting:
      return <EmailSubmitting />;
    case Stage.EmailRegistered:
      return (
        <EmailSubmitted
          email={fetcherData.email!}
          warning={t('login.stageComplete.notificationsWarning')}
        />
      );

    case Stage.Error:
      onDismiss({ loginSuccessful: false });
  }
}
